import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import "react-responsive-modal/styles.css";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import ApiController from "../../../controller/ApiController";
import { AreaType } from "../../../controller/MasterController";
import MessageController from "../../../controller/MessageController";
import { areaType } from "../../../recoil/area";
import {
  authState,
  authStoreType,
  authType,
  masterStoreState,
} from "../../../recoil/auth";
import "../../../styles/CustomModal.css";
import { SelectAreaModalProps, SelectedRoomType } from "./types";

export const useSelectArea = (props: SelectAreaModalProps) => {
  const navigate = useNavigate();

  // 현재 디바이스 방 설정
  const queryClient = useQueryClient();
  const deviceArea = queryClient.getQueryData<areaType | undefined>([
    "device-area",
  ]);

  const auth = useRecoilValue<authType>(authState);
  const master = useRecoilValue<authStoreType | null>(masterStoreState);

  // 선택 가능한 구역 목록
  const [areaList, setAreaList] = useState<AreaType[]>([]);
  const [roomList, setRoomList] = useState<SelectedRoomType | null>(null);

  // 선택한 구역
  const [selectedArea, setSelectedArea] = useState<number>(-1);
  const [selectedRoom, setSelectedRoom] = useState<number>(-1);

  // 전원, WIFI 상시, 비상시 여부, 메모
  const [devicePower, setDevicePower] = useState<number>(0);
  const [deviceWifiPower, setDeviceWifiPower] = useState<number>(0);
  const [deviceMemo, setDeviceMemo] = useState<string>("");

  // 구역 설정 안되어있으면 모달 닫기 불가능
  const onCloseModal = () => {
    if (
      !deviceArea ||
      deviceArea.area_number !== null ||
      deviceArea.number !== null
    )
      props.setVisible(false);
  };

  const navigateSettings = () => {
    navigate("/settings");
  };

  // App으로 하드웨어 명령 호출
  const sendPostMsgToApp = (type: string) => {
    switch (type) {
      case "offApp":
        console.log("앱종료");
        window.ReactNativeWebView?.postMessage(
          MessageController.getActionData("exit", {})
        );
        break;
      case "logout":
        console.log("로그아웃");
        window.ReactNativeWebView?.postMessage(
          MessageController.getActionData("logout", {})
        );
        break;

      case "shutdown":
        console.log("기기종료");
        window.ReactNativeWebView?.postMessage(
          MessageController.getActionData("shutdown", {})
        );
        break;

      case "reboot":
        console.log("기기 재시작");
        window.ReactNativeWebView?.postMessage(
          MessageController.getActionData("reboot", {})
        );
        break;

      case "restartApp":
        console.log("다시시작");
        window.ReactNativeWebView?.postMessage(
          MessageController.getActionData("restart", {})
        );
        break;
    }
  };

  const onChangeArea = (item: AreaType) => {
    setSelectedArea(item.area_number);
    setRoomList({
      area_number: item.area_number,
      room_list: item.room_list,
    });
  };

  // 구역 불러오기
  const getArea = async () => {
    try {
      if (props.storeId) {
        const response = await ApiController.area.getArea(props.storeId);
        console.log("getArea", response);

        if (response && response.data) {
          setAreaList(response.data.data);
          if (deviceArea) {
            setSelectedArea(deviceArea.area_number);
            setSelectedRoom(deviceArea.number);
          }

          const foundList = response.data.data.find(
            (item: AreaType) => item.area_number === deviceArea?.number
          );
          console.log("foundList", foundList);
          if (foundList)
            setRoomList({
              area_number: foundList.area_number,
              room_list: foundList.room_list,
            });
          getUserTokenInfo();
        }
      }
    } catch (err) {
      console.log("getArea err-->", err);
    }
  };

  // 구역 설정하기
  const setArea = async (isDisconnenct?: boolean) => {
    try {
      if (props.storeId && master) {
        if (isDisconnenct) {
          const response = await ApiController.area.setArea(
            props.storeId,
            null,
            null
          );
          console.log("setArea", response);

          if (response && response.data) {
            queryClient.fetchQuery({
              queryKey: ["device-area"],
              queryFn: () =>
                ApiController.area
                  .getAreaByUserToken(master.store_id)
                  .then((res) => res.data.data),
            });
            toast.success("객실 연결을 끊었어요");
            props.setVisible(false);
          }
        } else {
          const response = await ApiController.area.setArea(
            props.storeId,
            selectedArea,
            selectedRoom
          );
          console.log("setArea", response);

          if (response && response.data) {
            queryClient.fetchQuery({
              queryKey: ["device-area"],
              queryFn: () =>
                ApiController.area
                  .getAreaByUserToken(master.store_id)
                  .then((res) => res.data.data),
            });
            toast.success("구역을 설정했어요");
            props.setVisible(false);
          }
        }
      }
    } catch (err) {
      console.log("setArea err-->", err);
    }
  };

  // 구역 불러오기
  const getUserTokenInfo = async () => {
    try {
      const response = await ApiController.store.getUserTokenInfo();
      console.log("getUserTokenInfo", response);

      if (response && response.data) {
        const oRes = response.data;
        if (oRes.data) {
          setDeviceMemo(oRes.data.device_memo || "");
          setDevicePower(oRes.data.device_power);
          setDeviceWifiPower(oRes.data.device_wifi_power);
        }
      }
    } catch (err) {
      console.log("getArea err-->", err);
    }
  };

  // // 구역 설정하기
  const setUserTokenInfo = async () => {
    try {
      const response = await ApiController.store.setUserTokenInfo(
        devicePower,
        deviceMemo,
        deviceWifiPower
      );
      console.log("setUserTokenInfo", response);

      if (response && response.data) {
        toast.success("기기 설정을 저장했어요");
      }
    } catch (err) {
      console.log("setArea err-->", err);
    }
  };

  useEffect(() => {
    if (props.isVisible) {
      getArea();
    }
  }, [props.isVisible]);

  return {
    auth,
    areaList,
    roomList,
    selectedArea,
    selectedRoom,
    devicePower,
    deviceWifiPower,
    deviceMemo,
    deviceArea,
    onCloseModal,
    setSelectedRoom,
    setDevicePower,
    setDeviceWifiPower,
    setDeviceMemo,
    navigateSettings,
    sendPostMsgToApp,
    onChangeArea,
    setArea,
    setUserTokenInfo,
  };
};
